import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import GroupPicker, { IGroupPickerProps } from 'components/domain/pickers/GroupPicker/GroupPicker';
import { DEFAULT_PICKER_PAGE_SIZE, findOrFetchPickerItem } from 'components/foundation/pickers/utils';
import { useOpenable } from 'hooks';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import { useReactQueryLazyLoading } from 'hooks/react-query/useReactQueryLazyLoading/useReactQueryLazyLoading';
import useScrollerRef from 'hooks/ui/useScrollerRef';
import { ClassName } from 'types/ClassName';
import IMemberGroup from 'types/MemberGroup';
import { executeFunctionsCurried } from 'utils/functionUtils';
import { useEmployeeGroupPickerData } from './hooks/useEmployeeGroupPickerData';

export interface IEmployeeGroupPickerProps
  extends Omit<IGroupPickerProps, 'data' | 'isLoading' | 'currentGroup' | 'dataError' | 'loadedAll'> {
  filterByPermission?: boolean;
  className?: ClassName;
  excludeIds?: IMemberGroup['id'][];
}

function EmployeeGroupPicker({
  filterByPermission = false,
  excludeIds,
  value,
  className,
  onOpen,
  onClose,
  ...pickerProps
}: IEmployeeGroupPickerProps) {
  const toggle = useOpenable();
  const { scroller, setScrollerRef } = useScrollerRef();
  const baseKey = useReactQueryBaseKey();

  const { fetchPage, fetchSelected } = useEmployeeGroupPickerData({ filterByPermission, excludeIds });

  const {
    data,
    isLoading,
    isError: isDataError,
    loadedAll,
  } = useReactQueryLazyLoading(
    scroller,
    [...baseKey, 'member-group-picker', { filterByPermission, excludeIds }],
    fetchPage,
    DEFAULT_PICKER_PAGE_SIZE,
    { enabled: toggle.isOpen }
  );

  const { data: selectedGroup } = useQuery({
    queryKey: [...baseKey, 'member-group-picker-by-id', value],
    queryFn: () => findOrFetchPickerItem(value!, data, fetchSelected),
    enabled: !!value,
  });

  const classes = classNames('employee-picker', className);

  return (
    <GroupPicker
      currentGroup={selectedGroup?.groupName ?? null}
      className={classes}
      setScrollerRef={setScrollerRef}
      loadedAll={loadedAll}
      dataError={isDataError}
      data={data}
      isLoading={isLoading}
      onOpen={executeFunctionsCurried(toggle.open, onOpen)}
      onClose={executeFunctionsCurried(toggle.close, onClose)}
      value={value}
      {...pickerProps}
    />
  );
}

export default EmployeeGroupPicker;
